// Overlay router — reads call state, renders the right surface.
// Mounted once in AppLayout, sits at zIndex 500 (above modal scrims).
//
// InCallSurface is the only surface here that pulls livekit-client (~136KB
// gzipped), so it's loaded lazily — the chunk doesn't fetch until a call
// actually goes active. The other three surfaces stay statically imported:
// the incoming-ring modal in particular MUST appear instantly when a call
// rings (no chunk-fetch latency on the ring), and outgoing/ended are too
// small to bother splitting.

import { lazy, Suspense } from 'react'
import { useCallManager } from '@/lib/callManager'
import { IncomingCallModal } from './IncomingCallModal'
import { OutgoingCallOverlay } from './OutgoingCallOverlay'
import { CallEndedToast } from './CallEndedToast'
import { GroupIncomingCallModal } from './GroupIncomingCallModal'
import { GroupJoiningOverlay } from './GroupJoiningOverlay'

// Named export → default-shaped Promise for React.lazy.
const InCallSurface = lazy(() =>
  import('./InCallSurface').then(m => ({ default: m.InCallSurface }))
)

// Group counterpart — also lazy because it pulls livekit-client. Rollup
// will deduplicate the shared livekit-client dependency into one chunk
// reachable from either lazy entry, so the main bundle stays clean.
const GroupInCallSurface = lazy(() =>
  import('./GroupInCallSurface').then(m => ({ default: m.GroupInCallSurface }))
)

export function CallOverlay() {
  const { state } = useCallManager()
  switch (state.kind) {
    case 'incoming-ringing':       return <IncomingCallModal />
    case 'outgoing-ringing':       return <OutgoingCallOverlay />
    case 'active':                 return (
      <Suspense fallback={<InCallConnectingFallback peerName={state.peerName} />}>
        <InCallSurface />
      </Suspense>
    )
    case 'ended':                  return <CallEndedToast />
    case 'group-incoming-ringing': return <GroupIncomingCallModal />
    case 'group-joining':          return <GroupJoiningOverlay />
    case 'group-active':           return (
      <Suspense fallback={<InCallConnectingFallback peerName="Group call" />}>
        <GroupInCallSurface />
      </Suspense>
    )
    case 'idle':                   return null
  }
}

// Shared utility — initials from a name. Mirrors getInitials in lib/utils
// but keeps this component family self-contained for the spike.
export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase() || '?'
}

// Brief "Connecting…" stage shown while the InCallSurface chunk is being
// fetched. Matches the in-call dark aesthetic (same --call-stage-bg + the
// on-scrim ink tokens) so the transition into the call doesn't flash a
// stark white panel. Layout-stable with the eventual surface — same fixed
// inset, same z-index — so the swap is a content change, not a layout jump.
function InCallConnectingFallback({ peerName }: { peerName: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'var(--call-stage-bg)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        gap: 8,
        color: 'var(--on-scrim-light-ink)',
      }}
    >
      <div style={{
        fontSize: 'clamp(18px, 4vw, 22px)',
        fontWeight: 700,
        letterSpacing: '-0.01em',
      }}>
        {peerName || 'Call'}
      </div>
      <div style={{
        fontSize: 13,
        opacity: 0.7,
        letterSpacing: '0.02em',
      }}>
        Connecting…
      </div>
    </div>
  )
}
